import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Lock, Check, Eye, EyeOff } from 'lucide-react';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const codeFromQuery = searchParams.get('code') || searchParams.get('token') || '';
  const emailFromQuery = searchParams.get('email') || '';

  const [step, setStep] = useState<'request' | 'reset'>(codeFromQuery ? 'reset' : 'request');
  const [email, setEmail] = useState(emailFromQuery);
  const [resetCode, setResetCode] = useState(codeFromQuery);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setInfo('');
    
    try {
      await resetPassword({ username: email.trim() });
      setStep('reset');
      setInfo('Check your email for the verification code and complete the reset form.');
    } catch (_err) {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!resetCode.trim()) {
      setError('Reset code is required');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: resetCode.trim(),
        newPassword: password,
      });
      setIsSuccess(true);
    } catch (_err) {
      setError('Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          className="mb-4 -ml-4"
          onClick={() => navigate('/login')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to login
        </Button>

        <Card>
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Lock className="h-6 w-6 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center">
              {step === 'request' ? 'Forgot Password?' : 'Reset Password'}
            </CardTitle>
            <CardDescription className="text-center">
              {step === 'request' 
                ? 'Enter your email and we\'ll send you a reset link'
                : 'Enter your new password below'
              }
            </CardDescription>
          </CardHeader>

          <CardContent>
            {isSuccess ? (
              <div className="text-center space-y-4">
                <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center mx-auto">
                  <Check className="h-6 w-6 text-green-500" />
                </div>
                <p className="text-muted-foreground">
                  {step === 'request'
                    ? 'Check your email for a reset link'
                    : 'Password reset successfully!'
                  }
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => navigate('/login')}
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <form 
                onSubmit={step === 'request' ? handleRequestReset : handleResetPassword}
                className="space-y-4"
              >
                {step === 'request' ? (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Email</label>
                    <Input
                      type="email"
                      placeholder="name@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Email</label>
                      <Input
                        type="email"
                        placeholder="name@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Reset Code</label>
                      <Input
                        type="text"
                        placeholder="Enter verification code"
                        value={resetCode}
                        onChange={(e) => setResetCode(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">New Password</label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Must be at least 8 characters
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Confirm Password</label>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                    </div>
                  </>
                )}

                {error && (
                  <p className="text-sm text-red-500">{error}</p>
                )}

                {info && (
                  <p className="text-sm text-muted-foreground">{info}</p>
                )}

                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading 
                    ? (step === 'request' ? 'Sending...' : 'Resetting...')
                    : (step === 'request' ? 'Send Reset Link' : 'Reset Password')
                  }
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
